// /app/test/:testId/result — V2 test result (BRIEFS C7.4). Four figures (Total, Attempt %,
// Accuracy %, Score %), then one review card per question in the style of
// pages/student/TestResult.js (status colour + label), each with the answer panel (C11.2:
// correct answer, explanation, every notes reference). No Flag-Error / unlock mechanics in V2.
// A notes reference opens the Notes-with-Back view (C7.5) with "Back to your result"; on
// the way back the page scrolls to the same card. Practice Test answers show only here.
import React, { useEffect } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getTestResult } from '../../api/v2';
import { useActivityArea } from '../../components/app/activity';
import { IconArrowLeft, IconArrowRight } from '../../components/app/icons';
import {
  AnswerPanel, LETTERS, OptionRow, POINT_CSS, PyqBadge, QId, QuestionBody, card, refHref, resultPosKey, revealedState,
} from '../../components/app/testParts';
import { C, FONT, button } from '../../components/app/theme';
import { Loading } from '../../components/app/LoadState';

const STATUS = {
  correct: { color: C.good, bg: C.goodBg, label: 'Correct' },
  incorrect: { color: C.bad, bg: '#FEE2E2', label: 'Wrong' },
  unattempted: { color: '#64748B', bg: '#F1F5F9', label: 'Not answered' },
};

const pct = (n) => `${Math.round(Number(n || 0))}%`;
const num = (n) => (Number.isInteger(n) ? String(n) : String(Math.round(Number(n || 0) * 100) / 100));

function Figure({ label, value, sub, testId }) {
  return (
    <div data-testid={testId} style={{
      ...card, flex: '1 1 170px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{ fontSize: 13, color: C.muted }}>{label}</div>
      <div style={{ fontFamily: FONT.heading, fontSize: 30, fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ fontSize: 13, color: C.muted }}>{sub}</div>}
    </div>
  );
}

function ReviewCard({ q, testId }) {
  const st = STATUS[q.status] || STATUS.unattempted;
  return (
    <div id={`q-${q.number}`} data-testid="v2-review-card" data-status={q.status} style={{
      background: C.surface, border: `2px solid ${st.color}`, borderRadius: 12, padding: 18,
      display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span data-testid="v2-review-status" style={{
          background: st.bg, color: st.color, padding: '4px 10px', borderRadius: 8, fontWeight: 700, fontSize: 13,
        }}>Q{q.number} · {st.label}</span>
        <PyqBadge pyq={q.pyq} />
        <QId id={q.question_id} style={{ marginLeft: 'auto' }} />
      </div>
      <QuestionBody q={q} size={15} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {LETTERS.map((L) => (
          <OptionRow key={L} small letter={L} html={q[`option_${L.toLowerCase()}`]}
            imageUrl={q[`option_${L.toLowerCase()}_image_url`]}
            state={revealedState(L, q.your_answer, q.correct_option)} />
        ))}
      </div>
      <AnswerPanel correctOption={q.correct_option} explanation={q.explanation}
        imageUrl={q.explanation_image_url} refs={q.notes_refs} size={14}
        hrefFor={(ref) => refHref(ref, { testId, number: q.number, from: 'result' })}
        onOpenRef={() => {
          try { sessionStorage.setItem(resultPosKey(testId), String(q.number)); } catch { /* ignore */ }
        }} />
    </div>
  );
}

export default function AppTestResult() {
  const { testId } = useParams();
  useActivityArea('test');
  const navigate = useNavigate();
  const resQ = useQuery({
    queryKey: ['v2', 'test-result', testId],
    queryFn: () => getTestResult(testId),
    retry: (n, err) => !(err && err.response && [404, 409].includes(err.response.status)) && n < 2,
  });

  if (resQ.isError) {
    const s = resQ.error && resQ.error.response && resQ.error.response.status;
    if (s === 409) return <Navigate to={`/app/test/${testId}`} replace />; // not finished yet
    return (
      <div style={{ padding: 32, display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 560 }}>
        <div style={{ fontFamily: FONT.heading, fontSize: 22, fontWeight: 600 }}>{s === 404 ? 'This result was not found.' : 'The result could not load.'}</div>
        {s !== 404 && <div style={{ fontSize: 15, color: C.muted }}>Check the internet and press Try again.</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          {s !== 404 && <button type="button" style={button.primary} onClick={() => resQ.refetch()}>Try again</button>}
          <button type="button" style={button.secondary} onClick={() => navigate('/app')}>Back to Home</button>
        </div>
      </div>
    );
  }
  const r = resQ.data;
  return <ResultBody r={r} testId={testId} />;
}

function ResultBody({ r, testId }) {
  // back from the notes (C7.5): scroll to the card he left from
  useEffect(() => {
    if (!r) return;
    let n = null;
    try { n = sessionStorage.getItem(resultPosKey(testId)); sessionStorage.removeItem(resultPosKey(testId)); } catch { /* ignore */ }
    const el = n ? document.getElementById(`q-${n}`) : null;
    if (el) el.scrollIntoView({ block: 'start' });
  }, [r, testId]);

  if (!r) return <Loading text="Loading your result…" style={{ padding: 32 }} />;

  const again = r.mode === 'v2_chapter'
    ? { to: `/app/chapter-test${r.chapter_ids && r.chapter_ids[0] ? `?chapter=${r.chapter_ids[0]}` : ''}`, label: 'Another Chapter Test' }
    : { to: '/app/practice', label: 'Another Practice Test' };

  return (
    <div data-testid="v2-result" style={{ maxWidth: 900, margin: '0 auto', padding: '24px 20px 40px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <Link to="/app" style={{ ...button.secondary, display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', boxSizing: 'border-box' }}>
          <IconArrowLeft />Back to Home
        </Link>
        <Link to={again.to} data-testid="v2-again" style={{ ...button.primary, display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', boxSizing: 'border-box' }}>
          {again.label}<IconArrowRight />
        </Link>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h1 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 26, fontWeight: 600 }}>Your result</h1>
        <div style={{ fontSize: 15, color: C.muted }}>{r.title}</div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
        <Figure testId="v2-fig-total" label="Total" value={r.total} sub={`${r.attempted} answered · ${r.unattempted} not answered`} />
        <Figure testId="v2-fig-attempt" label="Attempt %" value={pct(r.attempt_pct)} sub={`${r.attempted} of ${r.total}`} />
        <Figure testId="v2-fig-accuracy" label="Accuracy %" value={pct(r.accuracy_pct)} sub={`${r.correct} right · ${r.incorrect} wrong`} />
        <Figure testId="v2-fig-score" label="Score %" value={pct(r.score_pct)} sub={`${num(r.score)} of ${num(r.max_score)} marks`} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <style>{POINT_CSS}</style>
        <h2 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 18, fontWeight: 600 }}>Every question</h2>
        {(r.questions || []).map((q) => <ReviewCard key={q.question_id} q={q} testId={testId} />)}
      </div>
    </div>
  );
}
